import React, { useState } from "react";
import dayjs from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import API from "../../api/axiosInstance";
import {
  utcToLADateKey,
  formatLADateKeyReadable,
  formatLADateKeyOrRangeForDisplay,
} from "../../utils/laDateUtils";
import "../css/RoomsInformation.css";
import ExtraServicesEditor from "./ExtraServicesEditor";
import { occupancyForBooking } from "./RoomsInformationCells";
import { allocationTypeLabelFromBooking, sendCancellationEmailRequest } from "./bookingCancellationEmail";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const sumServices = (list) => (list || []).reduce((s, x) => s + (Number(x.price) || 0), 0);
const initials = (name) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("") || "?";
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const nameOf = (person) => (person ? `${person.name || ""} ${person.lname || ""}`.trim() : "") || "(Unknown)";

/** One participant's booking: identity, quiet meta line, actions, then optional body + total. */
function BookingCard({ name, isGuestOnly, meta, summary, editLabel, onEdit, onCancel, roomTotal, servicesTotal, children }) {
  return (
    <div className="booking-cell-menu-card">
      <div className="booking-cell-menu-card-top">
        <span className="booking-cell-menu-avatar" aria-hidden="true">{initials(name)}</span>
        <div className="booking-cell-menu-card-who">
          <strong className="booking-cell-menu-card-name">
            {name}
            {isGuestOnly && <span className="rooms-info-guest-only-badge">Guest only</span>}
          </strong>
          <div className="booking-cell-menu-card-details">{meta}</div>
          {summary && <div className="booking-cell-menu-card-summary">{summary}</div>}
        </div>
        <div className="booking-cell-menu-card-actions">
          <button type="button" className="booking-cell-menu-btn" onClick={onEdit}>{editLabel}</button>
          {onCancel && (
            <button type="button" className="booking-cell-menu-btn booking-cell-menu-btn--danger" onClick={onCancel}>Cancel</button>
          )}
        </div>
      </div>
      {children}
      <div className="booking-cell-menu-card-total">
        <span>Room {money(roomTotal)} + Services {money(servicesTotal)}</span>
        <strong>Total {money(roomTotal + servicesTotal)}</strong>
      </div>
    </div>
  );
}

const BookingCellMenu = ({
  bookings, unit, date, bedCount, defaultPrice, isPast,
  onClose, onChanged, onAddAnother, onRefresh,
  groupedEntries, dateRange, onEditEntry,
  hotelName = "", roomType = "", competitionId = "",
}) => {
  const isRangeMode = !!groupedEntries;
  const [error, setError] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);
  // Quick-add persists per booking immediately (no form-submit step), so we
  // mirror each booking's extra_services locally — keyed by booking id —
  // to reflect a successful PATCH right away without waiting on the
  // parent's next full fetchBookings() (triggered silently via onRefresh).
  const [serviceOverrides, setServiceOverrides] = useState({});

  const servicesFor = (b) => serviceOverrides[b._id] ?? b.extra_services ?? [];
  const priceFor = (b) => (b.price != null ? b.price : defaultPrice);

  const handleServicesChange = async (b, newServices) => {
    const prev = servicesFor(b);
    setServiceOverrides((s) => ({ ...s, [b._id]: newServices }));
    try {
      await API.patch(`/ems/room-bookings/${b._id}`, { extra_services: newServices });
      if (onRefresh) onRefresh();
    } catch (err) {
      setServiceOverrides((s) => ({ ...s, [b._id]: prev }));
      setError(err.response?.data?.message || "Failed to update services");
    }
  };

  const singleDateKey = utcToLADateKey(date);

  const handleDelete = (b) => {
    setConfirmModal({
      message: "Cancel this booking?",
      onConfirm: async () => {
        setIsDeleting(true);
        try {
          await API.delete(`/ems/room-bookings/${b._id}`);
          await sendCancellationEmailRequest({
            cancelledBookings: [b],
            hotelName,
            roomType,
            roomLabel: unit?.room_label || "",
            competitionId,
          });
          onChanged("Booking cancelled successfully.");
        } catch (err) {
          setError(err.response?.data?.message || "Failed to cancel booking");
          setIsDeleting(false);
        }
      },
    });
  };

  const handleDeleteEntry = (entry) => {
    const count = entry.bookings.length;
    setError("");
    setConfirmModal({
      message: `Cancel ${count} booking${count > 1 ? "s" : ""} for this participant?`,
      onConfirm: async () => {
        setIsDeleting(true);
        const slice = [...entry.bookings];
        try {
          for (const b of slice) {
            await API.delete(`/ems/room-bookings/${b._id}`);
          }
          await sendCancellationEmailRequest({
            cancelledBookings: slice,
            hotelName,
            roomType,
            roomLabel: unit?.room_label || "",
            competitionId,
          });
          onChanged(`${count} booking${count > 1 ? "s" : ""} cancelled successfully.`);
        } catch (err) {
          setError(err.response?.data?.message || "Failed to cancel bookings");
          setIsDeleting(false);
        }
      },
    });
  };

  const handleEditSingle = (b) => {
    if (onEditEntry) {
      onEditEntry({
        participant: b.participant_id,
        dateLabel: formatLADateKeyReadable(singleDateKey),
        bookings: [b],
      });
    }
  };

  if (confirmModal) {
    return (
      <div className="booking-cell-menu-overlay" role="dialog" aria-modal="true">
        <div className="booking-cell-menu">
          <div className="booking-cell-menu-header">
            <h5 className="booking-cell-menu-title">Confirm Cancellation</h5>
          </div>
          <div className="booking-cell-menu-confirm-body">
            <p className="booking-cell-menu-confirm-message">{confirmModal.message}</p>
            {error && <div className="booking-cell-menu-error">{error}</div>}
          </div>
          <div className="booking-cell-menu-footer">
            <button type="button" className="btn btn-danger btn-sm" onClick={confirmModal.onConfirm} disabled={isDeleting}>
              {isDeleting
                ? <><FontAwesomeIcon icon={faCircleNotch} spin style={{ marginRight: "6px" }} />Cancelling…</>
                : "Yes, Cancel"}
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setConfirmModal(null); setError(""); }} disabled={isDeleting}>
              No
            </button>
          </div>
        </div>
      </div>
    );
  }

  const peopleLabel = (b) => plural(occupancyForBooking(b), "person", "people");
  const metaLine = (b, prefix = "") =>
    `${prefix}${allocationTypeLabelFromBooking(b)} · ${peopleLabel(b)} · $${priceFor(b)} / night`;

  let subtitle;
  let canAddAnother;
  if (isRangeMode) {
    const nights = dayjs(dateRange.to).diff(dayjs(dateRange.from), "day") + 1;
    subtitle = `${formatLADateKeyOrRangeForDisplay(`${dateRange.from} to ${dateRange.to}`)} · ${plural(nights, "night", "nights")}`;
    const occupancyByDate = {};
    for (const entry of groupedEntries) {
      for (const b of entry.bookings) {
        occupancyByDate[b.la_date_key] = (occupancyByDate[b.la_date_key] || 0) + occupancyForBooking(b);
      }
    }
    canAddAnother = Math.max(...Object.values(occupancyByDate), 0) < bedCount;
  } else {
    subtitle = `${dayjs(singleDateKey).format("ddd, D MMM YYYY")} · 1 night`;
    canAddAnother = bookings.reduce((s, x) => s + occupancyForBooking(x), 0) < bedCount;
  }

  return (
    <div className="booking-cell-menu-overlay" role="dialog" aria-modal="true" aria-labelledby="booking-cell-menu-title">
      <div className="booking-cell-menu">
        <div className="booking-cell-menu-header">
          <div>
            <h5 id="booking-cell-menu-title" className="booking-cell-menu-title">Room {unit.room_label}</h5>
            <div className="booking-cell-menu-subtitle">{subtitle}</div>
          </div>
          <button type="button" onClick={onClose} className="booking-cell-menu-close" aria-label="Close">&times;</button>
        </div>
        {error && <div className="booking-cell-menu-error">{error}</div>}

        {isRangeMode
          ? groupedEntries.map((entry, idx) => {
              const b = entry.bookings[0];
              const allServices = entry.bookings.flatMap((x) => x.extra_services || []);
              const servicesTotal = sumServices(allServices);
              return (
                <BookingCard
                  key={idx}
                  name={nameOf(entry.participant)}
                  isGuestOnly={b.is_guest_only}
                  meta={`${formatLADateKeyOrRangeForDisplay(entry.dateLabel)} · ${metaLine(b)}`}
                  summary={allServices.length > 0 ? `${plural(allServices.length, "service", "services")} · ${money(servicesTotal)}` : null}
                  editLabel="Edit"
                  onEdit={() => onEditEntry(entry)}
                  onCancel={() => handleDeleteEntry(entry)}
                  roomTotal={entry.bookings.reduce((s, x) => s + (Number(priceFor(x)) || 0), 0)}
                  servicesTotal={servicesTotal}
                />
              );
            })
          : bookings.map((b) => (
              <BookingCard
                key={b._id}
                name={nameOf(b.participant_id)}
                isGuestOnly={b.is_guest_only}
                meta={metaLine(b)}
                editLabel={isPast ? "Edit Price" : "Edit"}
                onEdit={() => handleEditSingle(b)}
                onCancel={isPast ? null : () => handleDelete(b)}
                roomTotal={Number(priceFor(b)) || 0}
                servicesTotal={sumServices(servicesFor(b))}
              >
                <ExtraServicesEditor
                  variant="menu"
                  services={servicesFor(b)}
                  onChange={(next) => handleServicesChange(b, next)}
                />
              </BookingCard>
            ))}

        <div className="booking-cell-menu-footer booking-cell-menu-footer--with-add">
          <div>
            {!isPast && onAddAnother && canAddAnother && (
              <button type="button" className="btn btn-sm btn-outline-warning" onClick={onAddAnother}>+ Add Another</button>
            )}
          </div>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default BookingCellMenu;
